import React, { useState, useEffect, useRef, forwardRef } from 'react';
// MUI components
import {
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
// Esri Loader
import { loadModules } from 'esri-loader';

const LocationPicker = forwardRef(function LocationPicker({ value, onChange }, ref) {
  // State to control the dialog's open/close status
  const [open, setOpen] = useState(false);

  // State to store the selected location
  const [location, setLocation] = useState(value || { lat: null, lon: null });

  // Refs to store map DOM node and view instance
  const mapDiv = useRef(null);
  const viewRef = useRef(null);

  // Initialize or update the map when the dialog opens
  useEffect(() => {
    if (open) {
      let view;

      const initializeMap = async () => {
        // Load the required ArcGIS modules
        const [EsriMap, MapView, Graphic, Search, BasemapToggle,esriConfig] = await loadModules(
          [
            'esri/Map',
            'esri/views/MapView',
            'esri/Graphic',
            'esri/widgets/Search',
            'esri/widgets/BasemapToggle',  "esri/config",
          ],
          {
            css: true, // Automatically injects the ArcGIS CSS
          }
        );
        esriConfig.apiKey = "AAPKe20358bbea714b54bbf850c7b10cffaeX7kOk8HsoAY1WVM_5YPAcuSLuB3GfCY0fCx3T3ajF5CqTVaLftdDDOn5SjQBnalO";
            
        if (!viewRef.current) {
          // Create a new map
          const map = new EsriMap({
            basemap: 'streets-navigation-vector', // Basemap style
          });

          // Create a new view
          view = new MapView({
            container: mapDiv.current, // Reference to the map div
            map: map,
            center: [0, 0], // Initial center point
            zoom: 2, // Initial zoom level
          });

           
          
          // Add BasemapGallery widget
          const basemapToggle = new BasemapToggle({
            view: view,
            nextBasemap: 'satellite',
          });
          view.ui.add(basemapToggle, {
            position: 'bottom-right',
          });

          
          const search = new Search({  //Add Search widget
            view: view
          });
          view.ui.add(search, {
            position: "top-right"
          });	

          // Map click event to add point and update location
          view.on('click', function (event) {
            // Remove existing graphics
            view.graphics.removeAll();

            // Create a new point graphic
            const pointGraphic = new Graphic({
              geometry: event.mapPoint,
              symbol: {
                type: 'simple-marker',
                color: 'red',
                size: '12px',
              },
            });

            // Add the graphic to the view
            view.graphics.add(pointGraphic);

            // Update location state
            setLocation({
              lat: event.mapPoint.latitude,
              lon: event.mapPoint.longitude,
            });
          });

          // If a location already exists, display it
          if (location.lat && location.lon) {
            view.when(() => {
              view.goTo({
                center: [location.lon, location.lat],
                zoom: 13,
              });

              const pointGraphic = new Graphic({
                geometry: {
                  type: 'point',
                  latitude: location.lat,
                  longitude: location.lon,
                },
                symbol: {
                  type: 'simple-marker',
                  color: 'red',
                  size: '12px',
                },
              });

              view.graphics.add(pointGraphic);
            });
          }

          // Store the view instance
          viewRef.current = view;
        } else {
          // Use the existing view
          view = viewRef.current;
          view.graphics.removeAll();

          if (location.lat && location.lon) {
            view.goTo({
              center: [location.lon, location.lat],
              zoom: 13,
            });

            const pointGraphic = new Graphic({
              geometry: {
                type: 'point',
                latitude: location.lat,
                longitude: location.lon,
              },
              symbol: {
                type: 'simple-marker',
                color: 'red',
                size: '12px',
              },
            });

            view.graphics.add(pointGraphic);
          } else {
            view.goTo({
              center: [0, 0],
              zoom: 2,
            });
          }
        }
      };

      initializeMap();

      // Cleanup function
      return () => {
        if (viewRef.current) {
          viewRef.current.destroy();
          viewRef.current = null;
        }
      };
    }
  }, [open]);

  // Handle dialog open
  const handleOpen = () => {
    setOpen(true);
  };

  // Handle dialog close without saving
  const handleClose = () => {
    setOpen(false);
  };

  // Handle save action
  const handleSave = () => {
    setOpen(false);
    if (onChange) {
      onChange(location);
    }
  };

  return (
    <>
      {/* Readonly input field with map icon */}
      <TextField
        label="Location"
        value={location.lat && location.lon ? `${location.lat}, ${location.lon}` : ''}
        inputRef={ref}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleOpen}>
                <MapIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Map dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Select Location</DialogTitle>
        <DialogContent>
          {/* Map container */}
          <div style={{ width: '100%', height: '500px' }} ref={mapDiv}></div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default LocationPicker;
